import { supabase } from '@/integrations/supabase/client';

/**
 * Generate audio for specific modules by module IDs
 */
export async function generateSpecificModuleAudio(moduleIds: string[]) {
  console.log(`🎵 Generating audio for ${moduleIds.length} specific modules...`);
  
  const results = {
    successful: [] as string[],
    failed: [] as { moduleId: string; error: string }[],
  };

  for (const moduleId of moduleIds) {
    try {
      // Get module and course info
      const { data: moduleData, error: moduleError } = await supabase
        .from('course_modules')
        .select('module_id, course_id, module_title')
        .eq('module_id', moduleId)
        .eq('is_enabled', true)
        .single();

      if (moduleError || !moduleData) {
        throw new Error(`Module not found or disabled: ${moduleId}`);
      }

      // Get module content
      const { data: contentData, error: contentError } = await supabase
        .from('module_content')
        .select('content')
        .eq('module_id', moduleId)
        .eq('course_id', moduleData.course_id)
        .single();

      if (contentError || !contentData?.content) {
        throw new Error(`No content found for module: ${moduleId}`);
      }

      console.log(`📝 Generating audio for: ${moduleData.module_title} (${moduleId})`);

      // Call direct audio generator
      const { data, error } = await supabase.functions.invoke('direct-audio-generator', {
        body: {
          moduleId: moduleId,
          courseId: moduleData.course_id,
        },
      });

      if (error) throw error;

      if (data?.success) {
        console.log(`✅ Audio generated: ${moduleId}`);
        results.successful.push(moduleId);
      } else {
        throw new Error(data?.error || 'Unknown error');
      }

      // Small delay between requests to avoid rate limits
      await new Promise(resolve => setTimeout(resolve, 1000));

    } catch (error: any) {
      console.error(`❌ Failed to generate audio for ${moduleId}:`, error.message);
      results.failed.push({ moduleId, error: error.message });
    }
  }

  console.log('\n📊 Generation Results:');
  console.log(`✅ Successful: ${results.successful.length}`);
  console.log(`❌ Failed: ${results.failed.length}`);
  
  if (results.failed.length > 0) {
    console.log('\n❌ Failed Modules:');
    results.failed.forEach(f => console.log(`  - ${f.moduleId}: ${f.error}`));
  }

  return results;
}

// List of 8 missing modules to generate
export const MISSING_MODULES = [
  'k8s-cluster-architecture',
  'performance-monitoring',
  'robotics-rl-basics',
  'advanced-rl-algorithms',
  'enterprise-architecture',
  'vercel-edge-functions',
  'smart-iot-systems',
  'unity-fundamentals'
];

/**
 * Generate audio for the 8 known missing modules
 */
export async function generateMissingModulesAudio() {
  console.log('🎯 Starting audio generation for 8 missing modules...');
  return await generateSpecificModuleAudio(MISSING_MODULES);
}
